import React, { useEffect, useState } from 'react';
import {
    Bell,
    BookOpen,
    Trophy,
    Megaphone,
    AlarmClock,
    CheckCircle,
    Clock,
    GraduationCap,
    X,
    Eye,
    FileText,
    Users,
    UserPlus,
    UserCheck,
    AlertCircle,
    Check,
    XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { API_BASE_URL } from '../../config/apiConfig';

export default function NotificationPage() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');
    const [processingRequest, setProcessingRequest] = useState(null);

    const goBack = () => navigate(-1);

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const res = await fetch(`${API_BASE_URL}/api/notifications`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!res.ok) {
                    console.error('Failed to fetch notifications:', await res.json());
                    return;
                }

                const data = await res.json();
                setNotifications(data.data || []);
                console.log(data)
                console.log(data.data)
            } catch (err) {
                console.error('Error fetching notifications:', err);
            }
        };

        fetchNotifications();
    }, []);

    const getIcon = (type) => {
        switch (type) {
            case 'assignment': return <BookOpen className="w-6 h-6" />;
            case 'grade': return <Trophy className="w-6 h-6" />;
            case 'announcement': return <Megaphone className="w-6 h-6" />;
            case 'reminder': return <AlarmClock className="w-6 h-6" />;
            case 'achievement': return <CheckCircle className="w-6 h-6" />;
            case 'exam': return <FileText className="w-6 h-6" />;
            case 'group_invite': return <Users className="w-6 h-6" />;
            case 'friend_request': return <UserPlus className="w-6 h-6" />;
            case 'group_request': return <UserCheck className="w-6 h-6" />;
            case 'system_alert': return <AlertCircle className="w-6 h-6" />;
            case 'custom': return <Bell className="w-6 h-6" />;
            default: return <Bell className="w-6 h-6" />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'assignment': return 'bg-blue-500';
            case 'grade': return 'bg-emerald-500';
            case 'announcement': return 'bg-indigo-500';
            case 'reminder': return 'bg-red-500';
            case 'achievement': return 'bg-amber-500';
            case 'exam': return 'bg-purple-500';
            case 'group_invite': return 'bg-cyan-500';
            case 'friend_request': return 'bg-pink-500';
            case 'group_request': return 'bg-teal-500';
            case 'system_alert': return 'bg-orange-500';
            case 'custom': return 'bg-gray-500';
            default: return 'bg-blue-500';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'border-l-red-500';
            case 'medium': return 'border-l-yellow-500';
            case 'low': return 'border-l-green-500';
            default: return 'border-l-blue-500';
        }
    };

    const filteredNotifications = notifications.filter(notification => {
        if (activeFilter === 'all') return true;
        if (activeFilter === 'unread') return notification.unread;
        if (activeFilter === 'requests') return notification.isRequest && notification.requestStatus === 'pending';
        return notification.type === activeFilter;
    });

    const unreadCount = notifications.filter(n => n.unread).length;
    const requestCount = notifications.filter(n => n.isRequest && n.requestStatus === 'pending').length;

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/notifications/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ unread: false }),
            });

            if (!res.ok) throw new Error('Failed to update notification');

            // Update local state
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, unread: false } : n)
            );
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
        }
    };


    const dismissNotification = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_BASE_URL}/api/notifications/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            setNotifications(notifications.filter(n => n.id !== id));
        } catch (err) {
            console.error('Failed to delete notification:', err);
        }
    };

    const handleRequestAction = async (notificationId, action, referenceId, type) => {
        setProcessingRequest(notificationId);
        try {
            const token = localStorage.getItem('token');

            // Update notification status
            await fetch(`${API_BASE_URL}/api/notifications/${notificationId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    requestStatus: action,
                    unread: false
                }),
            });

            // Handle the actual request based on type
            let endpoint = '';
            let method = 'POST';
            let body = {};

            switch (type) {
                case 'group_invite':
                    endpoint = `${API_BASE_URL}/api/groups/${referenceId}/respond-invite`;
                    body = { action }; // accepted or rejected
                    break;
                case 'friend_request':
                    endpoint = `${API_BASE_URL}/api/friends/${referenceId}/respond`;
                    body = { action };
                    break;
                case 'group_request':
                    endpoint = `${API_BASE_URL}/api/groups/${referenceId}/respond-request`;
                    body = { action };
                    break;
                default:
                    break;
            }

            if (endpoint) {
                await fetch(endpoint, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(body),
                });
            }

            // Update local state
            setNotifications(notifications.map(n =>
                n.id === notificationId
                    ? { ...n, requestStatus: action, unread: false }
                    : n
            ));
        } catch (err) {
            console.error('Failed to process request:', err);
        } finally {
            setProcessingRequest(null);
        }
    };

    const markAllAsRead = async () => {
        const token = localStorage.getItem('token');
        try {
            await Promise.all(
                notifications.filter(n => n.unread).map(n =>
                    fetch(`${API_BASE_URL}/api/notifications/${n.id}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ unread: false }),
                    })
                )
            );
            setNotifications(notifications.map(n => ({ ...n, unread: false })));
        } catch (err) {
            console.error('Failed to mark all notifications as read:', err);
        }
    };

    const filterTabs = [
        { key: 'all', label: 'All', icon: <Bell className="w-4 h-4" /> },
        { key: 'unread', label: 'Unread', icon: <Clock className="w-4 h-4" />, count: unreadCount },
        { key: 'requests', label: 'Requests', icon: <UserCheck className="w-4 h-4" />, count: requestCount },
        { key: 'assignment', label: 'Assignments', icon: <BookOpen className="w-4 h-4" /> },
        { key: 'exam', label: 'Exams', icon: <FileText className="w-4 h-4" /> },
        { key: 'grade', label: 'Grades', icon: <Trophy className="w-4 h-4" /> },
        { key: 'announcement', label: 'Announcements', icon: <Megaphone className="w-4 h-4" /> },
        { key: 'group_invite', label: 'Groups', icon: <Users className="w-4 h-4" /> },
        { key: 'friend_request', label: 'Friends', icon: <UserPlus className="w-4 h-4" /> }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
            <div className="flex items-center mb-6">
                <button onClick={goBack} className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition">
                    <FaArrowLeft className="text-xl" /> Back
                </button>
            </div>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-t-2xl shadow-lg border-b border-blue-100">
                    <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                                    <GraduationCap className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-white">TeeChaa Notifications</h1>
                                    <p className="text-blue-100">Stay updated with your academic progress</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                {requestCount > 0 && (
                                    <div className="flex items-center space-x-2 bg-pink-500 text-white px-4 py-2 rounded-full">
                                        <UserCheck className="w-5 h-5" />
                                        <span className="font-semibold">{requestCount} Pending</span>
                                    </div>
                                )}
                                {unreadCount > 0 && (
                                    <div className="flex items-center space-x-2 bg-red-500 text-white px-4 py-2 rounded-full animate-pulse">
                                        <Bell className="w-5 h-5" />
                                        <span className="font-semibold">{unreadCount} New</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Filter Tabs */}
                    <div className="p-4 bg-gray-50 border-b">
                        <div className="flex flex-wrap gap-2">
                            {filterTabs.map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveFilter(tab.key)}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeFilter === tab.key
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                                        }`}
                                >
                                    {tab.icon}
                                    <span>{tab.label}</span>
                                    {tab.count > 0 && (
                                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-1">
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Notifications List */}
                <div className="bg-white rounded-b-2xl shadow-lg">
                    <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                        {filteredNotifications.length === 0 ? (
                            <div className="text-center py-12">
                                <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-500 mb-2">No notifications found</h3>
                                <p className="text-gray-400">You're all caught up!</p>
                            </div>
                        ) : (
                            filteredNotifications.map((notification, index) => (
                                <div key={`${notification.id}-${index}`}
                                    className={`p-4 rounded-xl border-l-4 transition-all duration-200 hover:shadow-md ${notification.unread
                                        ? 'bg-blue-50 border-l-blue-500'
                                        : 'bg-white border-l-gray-200'
                                        } ${getPriorityColor(notification.priority)} ${notification.isRequest && notification.requestStatus === 'pending' ? 'ring-2 ring-pink-200' : ''}`}
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className={`p-3 rounded-full ${getTypeColor(notification.type)} text-white`}>
                                            {getIcon(notification.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <h3 className={`text-lg font-semibold ${notification.unread ? 'text-gray-900' : 'text-gray-700'}`}>
                                                            {notification.title}
                                                        </h3>
                                                        {notification.unread && (
                                                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                        )}
                                                        {notification.isRequest && (
                                                            <span className="bg-pink-100 text-pink-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                                                Action Required
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center space-x-2 mb-2">
                                                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                                            {notification.course}
                                                        </span>
                                                        <span className="text-gray-500 text-sm flex items-center">
                                                            <Clock className="w-4 h-4 mr-1" />
                                                            {notification.time}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-600 text-sm leading-relaxed mb-3">
                                                        {notification.message}
                                                    </p>

                                                    {/* Request Action Buttons */}
                                                    {notification.isRequest && notification.requestStatus === 'pending' && (
                                                        <div className="flex items-center space-x-2 mt-3">
                                                            <button
                                                                onClick={() => handleRequestAction(
                                                                    notification.id,
                                                                    'accepted',
                                                                    notification.reference_id,
                                                                    notification.type
                                                                )}
                                                                disabled={processingRequest === notification.id}
                                                                className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <Check className="w-4 h-4" />
                                                                <span>Accept</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleRequestAction(
                                                                    notification.id,
                                                                    'rejected',
                                                                    notification.reference_id,
                                                                    notification.type
                                                                )}
                                                                disabled={processingRequest === notification.id}
                                                                className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <XCircle className="w-4 h-4" />
                                                                <span>Decline</span>
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Request Status Display */}
                                                    {notification.isRequest && notification.requestStatus !== 'pending' && (
                                                        <div className={`flex items-center space-x-2 mt-3 px-3 py-2 rounded-lg ${notification.requestStatus === 'accepted'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-red-100 text-red-800'
                                                            }`}>
                                                            {notification.requestStatus === 'accepted' ? (
                                                                <CheckCircle className="w-4 h-4" />
                                                            ) : (
                                                                <XCircle className="w-4 h-4" />
                                                            )}
                                                            <span className="text-sm font-medium">
                                                                {notification.requestStatus === 'accepted' ? 'Accepted' : 'Declined'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {!notification.isRequest && (
                                                    <div className="flex items-center space-x-2 ml-4">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Mark as read"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); dismissNotification(notification.id); }}
                                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Dismiss"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer Actions */}
                    {notifications.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-b-2xl border-t">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">
                                    {filteredNotifications.length} of {notifications.length} notifications
                                </span>
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Mark All as Read</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}